import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, LIST, RECYCLE_INDEX } from "const"
import { MtDriveFile, MtDriveFolder } from "server/models"
import {
    deepCopy,
    hasAccess,
    hasProjectAccess,
    OrderType,
    StateType,
} from "client/utils"
import {
    PRM_DRIVE,
    StoreType,
    DriveFolder,
    DriveFile,
    AccountUserList,

    LoadNestedFolders,
    LoadDriveFolders,
    LoadDriveFolderById,
    LoadDriveFolderByProjectId,
    LoadDriveFiles,
    LoadDriveFileByName,
    LoadAccountUsers,
    LoadProjectFolderAccessData,
    GetProjectViewAccess,

    setAccountUserList,
    setDrivePath,
    setDriveFilters,

    setDriveFolderList,
    setDriveFolderListEmpty,
    setDriveFolderListLoadState,

    setDriveFileList,
    setDriveFileOffset,
    setDriveFileLoadMore,
    setDriveFileListEmpty,
    setDriveFileListLoadState,

    parseName,
    createDriveUrl,
    createFileName,
    createRecycleFileName,
    isProjectFolderOpened,
    getDriveScrollableParent,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Loader,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import SearchPanel from "./searchPanel"
import BreadCrumbs from "./breadCrumbs"
import DriveContent from "./content"
import { OpenFile } from "./actions/open"


interface DriveListProps {
    path?: string
}

const List = ({
    path = "",
}: DriveListProps) => {
    const onSelectFile = useSelector((state: StoreType) => state.drive.onSelectFile)
    const onSelectFiles = useSelector((state: StoreType) => state.drive.onSelectFiles)
    const onSelectVersion = useSelector((state: StoreType) => state.drive.onSelectVersion)
    const onSelect = onSelectFile || onSelectFiles || onSelectVersion

    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const baseRoute = useSelector((state: StoreType) => state.drive.baseRoute)
    const sortColumn = useSelector((state: StoreType) => state.drive.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.drive.sortOrder)
    const searchState = useSelector((state: StoreType) => state.drive.searchState)

    const folderList = useSelector((state: StoreType) => state.drive.folder.folderList)
    const folderListLoadState = useSelector((state: StoreType) => state.drive.folder.listLoadState)
    const folderListReload = useSelector((state: StoreType) => state.drive.folder.reloadList)

    const fileList = useSelector((state: StoreType) => state.drive.file.fileList)
    const fileListLoadState = useSelector((state: StoreType) => state.drive.file.listLoadState)
    const fileListReload = useSelector((state: StoreType) => state.drive.file.reloadList)

    const offset = useSelector((state: StoreType) => state.drive.file.offset)
    const limit = useSelector((state: StoreType) => state.drive.file.limit)
    const loadMore = useSelector((state: StoreType) => state.drive.file.loadMore)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadNestedFolders, setLoadNestedFolders] = useState(true)
    const [nestedFoldersLoadState, setNestedFoldersLoadState] = useState<StateType>(IDLE)
    const [accountUsersLoadState, setAccountUsersLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        setAccountUsersLoadState(PENDING)
        LoadAccountUsers(userData.accountId).then((accountUsers) => {
            const newAccountUserList: AccountUserList = { [userData.accountId]: accountUsers }
            dispatch(setAccountUserList(newAccountUserList))
            setAccountUsersLoadState(IDLE)
        })
    }, [])

    useEffect(() => {
        if (!baseRoute) return

        // load nested folder structure only on page load
        if (!loadNestedFolders) return

        // prevent loading top level if inner level is loading
        if (folderListLoadState === PENDING || fileListLoadState === PENDING) return

        // load all folders in the root and only project folders when project is opened
        const promise = !project.id
            ? LoadNestedFolders(userData.accountId, null, lang)
            : LoadDriveFolderByProjectId(project.id).then((projectFolder) => (
                LoadNestedFolders(userData.accountId, projectFolder, lang)
            ))

        setNestedFoldersLoadState(PENDING)
        promise.then((nestedFolders) => {
            // split path from the url
            let pathSplited: string[] = []
            path.split("/").forEach((pathItem) => {
                pathSplited.push(decodeURIComponent(pathItem))
            })
            pathSplited = pathSplited.filter((elem) => elem !== "")

            // open the parsed url
            let newDrivePath: MtDriveFolder[] = [nestedFolders.folder]
            let searchFolders = nestedFolders.folders
            const promiseArray: Promise<any>[] = []

            for (let i = 0; i < pathSplited.length; i++) {
                const pathItem = pathSplited[i]
                if (searchFolders[pathItem]) {
                    const nested = searchFolders[pathItem]
                    searchFolders = nested.folders
                    newDrivePath.push(nested.folder)

                    // load project data to define access to the folder
                    if (nested.folder.project) {
                        const projectId = nested.folder.project.id
                        promiseArray.push(LoadProjectFolderAccessData(projectId, userData, dispatch))
                    }
                } else if (i === pathSplited.length - 1) { // last path item is a file name
                    openFileByPath(newDrivePath, pathItem)
                } else {
                    newDrivePath = null
                    break
                }
            }

            Promise.all(promiseArray).then(() => {
                dispatch(setDrivePath(newDrivePath))
                setNestedFoldersLoadState(IDLE)
                setLoadNestedFolders(false)
            })
        })
    }, [
        baseRoute,
        loadNestedFolders,
        folderListLoadState,
        fileListLoadState,
    ])

    const LoadFolders = (
        drivePathArray: readonly MtDriveFolder[],
        column: keyof MtDriveFolder,
        order: OrderType,
    ) => {
        dispatch(setDriveFolderListEmpty(false))

        if (!drivePathArray) {
            dispatch(setDriveFolderList(null))
            return
        }

        const targetFolderId = drivePathArray.length > 0
            ? drivePathArray[drivePathArray.length - 1].id
            : 0 // root parent folder ID

        dispatch(setDriveFolderListLoadState(PENDING))
        LoadDriveFolderById(targetFolderId) // check if target folder exists
        .then((resTargetFolder) => {
            if (!resTargetFolder && targetFolderId) {
                dispatch(setDriveFolderListLoadState(IDLE))
                dispatch(setDriveFolderList(null))
            } else {
                LoadDriveFolders(
                    userData.accountId,
                    targetFolderId,
                    column,
                    order,
                ).then((res) => {
                    dispatch(setDriveFolderListLoadState(IDLE))
                    const folders: DriveFolder[] = []
                    res.forEach((folder) => {
                        const newDrivePath: MtDriveFolder[] = deepCopy(drivePathArray)
                        newDrivePath.push(folder)
                        folders.push({
                            ...folder,
                            url: createDriveUrl(newDrivePath, baseRoute),
                            path: newDrivePath,
                        })
                    })
                    dispatch(setDriveFolderList(folders))
                    if (folders.length === 0) {
                        dispatch(setDriveFolderListEmpty(true))
                    }
                })
            }
        })
    }

    const LoadFiles = (
        drivePathArray: readonly MtDriveFolder[],
        column: keyof MtDriveFile,
        order: OrderType,
        offsetValue: number,
    ) => {
        dispatch(setDriveFileListEmpty(false))

        if (!drivePathArray) {
            dispatch(setDriveFileList(null))
            return
        }

        const targetFolderId = drivePathArray.length > 0
            ? drivePathArray[drivePathArray.length - 1].id
            : 0 // root parent folder ID

        dispatch(setDriveFileListLoadState(PENDING))
        LoadDriveFiles(
            userData.accountId,
            targetFolderId,
            column,
            order,
            offsetValue,
            limit,
        ).then((res) => {
            const files: DriveFile[] = []
            res.forEach((file) => {
                const folderUrl = createDriveUrl(drivePathArray, baseRoute)
                const name = createFileName(file)
                const recycleName = createRecycleFileName(file)
                files.push({
                    ...file,
                    url: file.folderId === null
                        ? `${folderUrl}/${encodeURIComponent(recycleName)}`
                        : `${folderUrl}/${encodeURIComponent(name)}`,
                })
            })

            dispatch(setDriveFileListLoadState(IDLE))
            if (offsetValue === 0) {
                dispatch(setDriveFileList(files))
                dispatch(setDriveFileOffset(files.length))

                if (files.length === 0) {
                    dispatch(setDriveFileListEmpty(true))
                }
            } else {
                dispatch(setDriveFileList(fileList.concat(files)))
                dispatch(setDriveFileOffset(offsetValue + files.length))

                if ((!fileList || fileList.length === 0) && files.length === 0) {
                    dispatch(setDriveFileListEmpty(true))
                }
            }

            if (files.length === limit) {
                dispatch(setDriveFileLoadMore(true))
            } else {
                dispatch(setDriveFileLoadMore(false))
            }
        })
    }

    useEffect(() => {
        // load folder list
        if (!baseRoute) return
        if (!folderListReload) return

        dispatch(setDriveFilters({}))
        LoadFolders(drivePath, sortColumn as keyof MtDriveFolder, sortOrder)
    }, [folderListReload, drivePath, baseRoute, sortColumn, sortOrder])

    useEffect(() => {
        // load file list
        if (!baseRoute) return
        if (!fileListReload) return

        dispatch(setDriveFilters({}))
        LoadFiles(drivePath, sortColumn as keyof MtDriveFile, sortOrder, 0)
    }, [fileListReload, drivePath, baseRoute, sortColumn, sortOrder])

    useEffect(() => {
        // load files on page scroll
        if (!baseRoute) return null
        const scrollableParent = getDriveScrollableParent(onSelect)

        const onScroll = () => {
            if (nestedFoldersLoadState === PENDING
                || folderListLoadState === PENDING
                || fileListLoadState === PENDING
                || !loadMore) return

            const maxScroll = scrollableParent.scrollHeight
            const currentScroll = scrollableParent.scrollTop
            const windowHeight = window.innerHeight

            const diff = maxScroll - currentScroll - windowHeight
            if (currentScroll !== 0 && diff < 100) {
                LoadFiles(drivePath, sortColumn as keyof MtDriveFile, sortOrder, offset)
            }
        }

        if (scrollableParent) {
            scrollableParent.addEventListener("scroll", onScroll)
        }

        return () => {
            if (scrollableParent) {
                scrollableParent.removeEventListener("scroll", onScroll)
            }
        }
    }, [
        nestedFoldersLoadState,
        folderListLoadState,
        fileListLoadState,
        loadMore,
        drivePath,
        baseRoute,
        sortColumn,
        sortOrder,
        offset,
    ])

    const openFileByPath = (
        drivePathArray: MtDriveFolder[],
        filePath: string,
    ) => {
        let fileId: number
        let fileName: string
        const index = filePath.lastIndexOf(RECYCLE_INDEX) // file in the root of recycle
        if (index > 0) {
            fileId = Number(filePath.substring(index + RECYCLE_INDEX.length)) || null
            fileName = filePath.substring(0, index)
        } else {
            fileId = null
            fileName = filePath
        }

        if (!fileId) {
            const parentFolder = drivePathArray[drivePathArray.length - 1]
            const parsedName = parseName(fileName)
            LoadDriveFileByName(parentFolder.id, parsedName.name, parsedName.counter)
            .then((resFile) => {
                OpenFile(resFile ? resFile.id : 0, null, drivePathArray, baseRoute, onSelect, dispatch)
            })
        } else {
            OpenFile(fileId, null, drivePathArray, baseRoute, onSelect, dispatch)
        }
    }

    const projectId = isProjectFolderOpened(drivePath)
    const allowedToViewProject = GetProjectViewAccess(projectId)

    const accountListAccess = hasAccess(PRM_DRIVE, LIST)
    const projectListAccess = hasProjectAccess(PRM_DRIVE, LIST, projectId)

    const listAccess = !projectId
        ? accountListAccess
        : allowedToViewProject && projectListAccess

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.drive}</PageTitle>
            </PageHeader>

            <SearchPanel />
            <BreadCrumbs />

            <PageContent>
                {
                    !listAccess
                    && <AccessDenied tabMode />
                }

                {
                    (
                        nestedFoldersLoadState === PENDING
                        || folderListLoadState === PENDING
                        || fileListLoadState === PENDING
                        || searchState === PENDING
                        || accountUsersLoadState === PENDING
                    ) && <Loader />
                }

                {
                    listAccess && (!folderList || !fileList)
                    && <PageForm>{lang.drivePathNotExist}</PageForm>
                }
                {
                    listAccess && folderList && fileList
                    && <DriveContent />
                }
            </PageContent>
        </Container>
    )
}
export default List
